import { useContext, useState } from 'react';
import { any, string, object, func, bool, array } from 'prop-types';
import { Spin } from 'antd';

import { ProgressiveImg } from 'components/base';
import HeadSEO from 'components/base/headSEO';
import Layout from 'components/layout';
import Breadcrumb from 'components/breadcrumb';
import Map from 'components/map';
import StoresGrid, { Scheduler, StoreTypeLabel } from 'components/storesGrid';
import AppointmentModal from 'components/appointmentModal';
import { ShopInStoreSVG } from 'components/iconsSVG/shopInStore';
import { PhoneOutlineSVG } from 'components/iconsSVG/phoneOutline';
import { CalendarSVG } from 'components/iconsSVG/calendar';
import { GoToStoreOutlineSVG } from 'components/iconsSVG/goToStoreOutline';
import { WhatsappOutlineSVG } from 'components/iconsSVG/whatsappOutline';
import StoryImagesSlider from 'components/storyImagesSlider';
import { i18nContext } from 'context/i18n';
import { getIsMobileSSR, getLang } from 'utils/helpers';
import { dataLayerHandleEvent } from 'utils/dataLayers';
import { initCMS } from 'utils/cms';
import { StoreStatus } from 'components/store-status';
import { getStore, getAllStoresByCountry } from 'actions/shops';
import { checkProfile } from 'actions/general';

import { normalizeUrl } from 'utils/StringUtils';
import { getDomainValue } from 'utils/domain';

import styles from './style.module.css';

const ContactResumeBar = ({ telephone, bookingAvailability, whatsappPhone, urlGoogleMaps, name, t, handleBookAppointment, light = false }) => (
  <ul className={!light ? styles.resumeList : `${styles.resumeList} ${styles.resumeListLight}`}>
    {name && <li className={styles.resumeName}>{name}</li>}
    {telephone && (
      <a href={`tel:${telephone}`}>
        <li className={`${styles.resumePhone} ${!telephone && 'escondido'}`}>
          <PhoneOutlineSVG />
          <span>{t('tienda', 'call.us', 'Call us')}</span>
        </li>
      </a>
    )}
    {bookingAvailability && (
      <a>
        <li onClick={handleBookAppointment}>
          <CalendarSVG height="22" width="22" />
          <span>{t('tienda', 'book.your.appointment', 'Book your appointment')}</span>
        </li>
      </a>
    )}
    {whatsappPhone && (
      <a href={`https://api.whatsapp.com/send?phone=${whatsappPhone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer">
        <li>
          <WhatsappOutlineSVG height="26" width="26" />
          <span>{t('generico', 'whatsapp', 'Whatsapp')}</span>
        </li>
      </a>
    )}
    {urlGoogleMaps && (
      <a className={styles.goToStore} target="_blank" href={urlGoogleMaps} rel="noreferrer">
        <li>
          <GoToStoreOutlineSVG className={styles.goToStore} />
          <span>{t('tiendas.locator', 'go.to.store', 'Go to store')}</span>
        </li>
      </a>
    )}
  </ul>
);

ContactResumeBar.propTypes = {
  telephone: string,
  bookingAvailability: any,
  whatsappPhone: string,
  urlGoogleMaps: string,
  t: func,
  handleBookAppointment: func,
  light: bool,
  name: string,
};

const baseUrl = getDomainValue({
  camper: 'https://www.camper.com',
  nnormal: 'https://www.nnormal.com',
  camperlab: 'https://www.camperlab.com',
});
const { INTEGRA_DOMAIN } = process.env;

const schemaMapper = {
  8: 'Sunday',
  2: 'Monday',
  3: 'Tuesday',
  4: 'Wednesday',
  5: 'Thursday',
  6: 'Friday',
  7: 'Saturday',
};

export default function StorePage({ defaultProps, store, isMobile, allStoresByCountry, shoppingLinks }) {
  const { t, locale } = useContext(i18nContext);
  const [loading] = useState(false);

  // FIXME: No usa el set
  const [showingMap, setShowingMap] = useState(false);
  const [appointmentVisible, setAppointmentVisible] = useState(false);
  const {
    name,
    address1,
    postalCode,
    cityName,
    telephone,
    latitude,
    longitude,
    bookingAvailability,
    url,
    featMen,
    featWomen,
    featKids,
    featBags,
    storePickup,
    storeHours,
    status,
    statusText,
    relatedStores,
    whatsappPhone,
    urlGoogleMaps,
    takeback,
    camperLab,
    id,
    countryCode,
    images,
  } = store;

  const lang = getLang(locale);
  let countryName = '';
  try {
    const country = countryCode || '';
    const getCountryName = new Intl.DisplayNames([lang.toLowerCase() || 'en'], { type: 'region' });
    countryName = getCountryName.of(country.toUpperCase()).toLocaleLowerCase();
  } catch (error) {
    console.error('Country name not found');
  }

  const breadcrumbFragments = [
    {
      title: t('generico', 'breadcrumb.home', '**Home'),
      href: `/${locale}`,
    },
    {
      title: 'Shops',
      href: `/${locale}/shops`,
    },
  ];
  if (store?.url) {
    const path = store.url.split('/');
    path.shift();
    let currentHref = `${locale}/shops`;
    path.map((fragment, index) => {
      let modifiedFragment = fragment;

      if (fragment.toLowerCase() === 'espana') {
        modifiedFragment = modifiedFragment.replace('n', 'ñ');
      }

      currentHref = `${currentHref}/${modifiedFragment}`;
      const capitalizedTitle = modifiedFragment.charAt(0).toUpperCase() + modifiedFragment.slice(1);
      const isLastFragment = index + 1 === path.length;

      breadcrumbFragments.push({
        title: !isLastFragment ? capitalizedTitle : store.name,
        href: normalizeUrl(currentHref.startsWith('/') ? currentHref : `/${currentHref}`),
      });
      return null;
    });
  }

  const handleBookAppointment = () => {
    setAppointmentVisible((current) => !current);
  };

  const collections = [
    {
      label: t('tienda', 'men.collection', 'Men collection'),
      available: featMen,
      logo: '/assets-new/img/shops/men.png',
    },
    {
      label: t('tienda', 'women.collection', 'Women collection'),
      available: featWomen,
      logo: '/assets-new/img/shops/women.png',
    },
    {
      label: t('tienda', 'kids.collection', 'Kids collection'),
      available: featKids,
      logo: '/assets-new/img/shops/kids.png',
    },
    {
      label: t('tienda', 'bags.collection', 'Bags & accesories'),
      available: featBags,
      logo: '/assets-new/img/shops/accesories.png',
    },
  ];
  const services = [
    {
      label: t('tienda', 'shoe.care', 'Shoe Care'),
      available: false,
      logo: '/assets-new/img/shops/men.png',
    },
    {
      label: t('tienda', 'foot.care', 'Foot Care'),
      available: false,
      logo: '/assets-new/img/shops/women.png',
    },
    {
      label: t('tienda', 'takeback', 'Take Back'),
      available: takeback,
      logo: '/assets-new/img/shops/takeback.png',
    },
    {
      label: t('tienda', 'camperlab', 'Camperlab'),
      available: camperLab,
      logo: '/assets-new/img/shops/camperlab.png',
    },
    {
      label: t('tienda', 'ship.in.store', 'Ship in store'),
      available: storePickup,
      svg: <ShopInStoreSVG />,
    },
  ];

  let imageCover = false;
  let imagesStories = [];
  if (images && images.length > 0) {
    const imageFiltered = images.filter((image) =>
      image
        .split('/')
        .pop()
        .includes(isMobile ? '_M' : '_F'),
    );
    if (imageFiltered.length > 0) {
      [imageCover] = imageFiltered;
    }
    const imagesStoriesFiltered = images.filter((image) => image.split('/').pop().includes('_0'));
    if (imagesStoriesFiltered.length > 0) {
      imagesStories = imagesStoriesFiltered;
    }
  }

  const MainImgComponent = () =>
    imageCover ?
      <ProgressiveImg className={styles.storeImg} src={imageCover} alt={`${name} store`} />
    : <img src={'/assets-new/storeNoIMG.jpg'} alt={'Store no image'} className={styles.storeImg} />;
  const onClickTelephone = (whatsapp = false) => {
    dataLayerHandleEvent({
      event: `store_${!whatsapp ? 'call' : 'whatsapp'}`,
      event_category: 'store',
      event_detail_1: id,
      event_detail_2: name?.toLowerCase() || '',
    });
  };
  const onClickAppointmentBtn = () => {
    dataLayerHandleEvent({
      event: 'store_personal_shopper',
      event_category: 'store',
      event_detail_1: id,
      event_detail_2: name?.toLowerCase() || '',
    });
    setAppointmentVisible(true);
  };

  const pageTitle = `${t('tiendas.locator', 'tienda.de.zapatos.camper', 'Tienda de Zapatos')} ${name || 'Camper'}`;
  const canonicalUrl = url.includes('/shops') ? url : `/shops${url}`;
  const urlBack = `${INTEGRA_DOMAIN}/${locale}/shops?store=${url ? url.split('/').pop() : ''}`;

  const googleJSON = {
    '@context': 'http://schema.org',
    '@type': 'Store',
    name,
    telephone,
    url: `https://www.camper.com/${locale}/shops${url}`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: address1,
      addressLocality: cityName?.charAt(0)?.toUpperCase() + cityName?.slice(1)?.toLowerCase(),
      postalCode,
      addressCountry: store?.countryCode?.toUpperCase(),
    },
    branchOf: {
      '@type': 'Organization',
      '@id': 'https://www.camper.com',
      name: 'Camper',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude,
      longitude,
    },
    openingHoursSpecification: storeHours
      .filter((item) => {
        const { timetable } = item;
        const [opening, closing] = timetable.split(' - ');

        return opening !== undefined && closing !== undefined;
      })
      .map((item) => {
        const { timetable, dayOfWeek } = item;
        const [opening, closing] = timetable.split(' - ');

        return {
          '@type': 'OpeningHoursSpecification',
          dayOfTheWeek: `http://schema.org/${schemaMapper[dayOfWeek]}`,
          opens: opening,
          closes: closing,
        };
      }),
    currenciesAccepted: store.currency,
  };

  if (imageCover) {
    googleJSON.image = imageCover;
  }

  const acceptedPayments = ['Credit Card'];

  if (store.paymentCash) {
    acceptedPayments.unshift('Cash');
  }

  googleJSON.paymentAccepted = acceptedPayments;

  const googleScript = <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(googleJSON) }} />;

  return (
    <Layout
      {...defaultProps}
      breadcrumbs={false}
      extratags={[<link key="canonical" rel="canonical" href={normalizeUrl(`${baseUrl}/${locale}${canonicalUrl}`)} />]}
      metatags={{ titlePage: pageTitle }}
      pageName="ShoplocatorStore"
    >
      <HeadSEO description="Store" scripts={[googleScript]} />
      <Spin spinning={loading}>
        <div className={styles.storeContainer}>
          <a href={urlBack} className={styles.urlBack}>
            <span className={styles.caret}>{'<'}</span> <span className={styles.text}>{t('tiendas.locator', 'back.to.stores')}</span>
          </a>
          <div className={`${styles.storePhoto} ${showingMap ? styles.storeMap : ''} ${isMobile && (showingMap || !imageCover) ? styles.storeMapMobile : ''}`}>
            {showingMap || !imageCover ?
              <>
                <Map visible pins={[{ latitude, longitude }]} center={[latitude, longitude]} pageTitle={pageTitle} />
              </>
            : <MainImgComponent />}
          </div>
          <div className={styles.storeInfoWrapper}>
            <div className={styles.section1}>
              <div className={styles.storeHeader}>
                <StoreTypeLabel store={store} t={t} />
                <h1 className={styles.storeName}>{name}</h1>
                <div className={styles.storeContactWrapper}>
                  <ul className={styles.storeAddress}>
                    <li>{address1}</li>
                    <li>
                      {postalCode} - {cityName}
                    </li>
                    {countryName && <li className={styles.countryName}>{countryName}</li>}
                  </ul>
                  {/* <a className={styles.viewInMap} onClick={handleViewInMap}>{t('tiendas.locator', 'view.in.map', 'View in map')}</a> */}
                </div>
              </div>
            </div>
            <div className={styles.section2}>
              <div className={styles.scheduler}>
                <StoreStatus status={status} statusText={statusText} />
                <Scheduler schedule={storeHours} storeName={name} storeId={id} />
              </div>
              <div className={styles.storeContact}>
                <ul className={styles.contactList}>
                  {telephone && (
                    <li className={styles.contactPhone}>
                      <a onClick={() => onClickTelephone(false)} className={styles.contactLink} href={`tel:${telephone}`}>
                        <PhoneOutlineSVG />
                        {telephone}
                      </a>
                    </li>
                  )}
                  {whatsappPhone && (
                    <li className={styles.contactWhatsapp}>
                      <a
                        onClick={() => onClickTelephone(true)}
                        className={styles.contactLink}
                        href={`https://api.whatsapp.com/send?phone=${whatsappPhone.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <WhatsappOutlineSVG height={22} width={22} />
                        {whatsappPhone}
                      </a>
                    </li>
                  )}
                </ul>
              </div>
              <div>
                {urlGoogleMaps && (
                  <>
                    <a className={styles.urlGoogleMaps} href={urlGoogleMaps} rel="noreferrer" target="_blank">
                      {t('tiendas.locator', 'get.diretions', 'Get directions')}
                      <img src="/assets-new/img/shops/arrow.png" alt="chevron-right" />
                    </a>
                    <br></br>
                  </>
                )}
                {bookingAvailability && (
                  <button className={styles.bookAppointmentBtn} onClick={onClickAppointmentBtn}>
                    {t('tienda', 'book.your.appointment', 'Book your appointment')}
                    <img src="/assets-new/img/shops/arrow.png" alt="chevron-right" />
                  </button>
                )}
              </div>
              <div className={styles.features}>
                <ul className={styles.storeCollections}>
                  {collections
                    .filter((item) => item.available)
                    .map((item, index) => (
                      <li key={`collections-item-${index}`}>
                        {item.logo ?
                          <img src={item.logo} className={styles.featLogo} alt={item.label} />
                        : item.svg}
                        {item.label}
                      </li>
                    ))}
                </ul>
                <ul className={styles.storeCollections}>
                  {services
                    .filter((item) => item.available)
                    .map((item, index) => (
                      <li key={`collections-item-${index}`}>
                        {item.logo ?
                          <img src={item.logo} className={styles.featLogo} alt={item.label} />
                        : item.svg}
                        {item.label}
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Spin>
      {imagesStories.length > 0 && (
        <div className={styles.storeStoryContainer}>
          {!isMobile ?
            imagesStories.map((imgUrl) => (
              <div key={`store-story-${imgUrl}`} className={styles.storyBlock}>
                <img className={styles.storyImg} src={imgUrl} alt={`${name} store`} />
              </div>
            ))
          : <div className={styles.storyBlock}>
              <StoryImagesSlider images={imagesStories} />
            </div>
          }
          {!isMobile && (
            <div className={`${styles.storyBlock} ${styles.storyBlockText} ${styles.storyBlockDesktop}`}>
              <div dangerouslySetInnerHTML={{ __html: t('tienda', `historia.descripcion.${id}`) }} />
              <div>
                {urlGoogleMaps && (
                  <>
                    <a className={styles.urlGoogleMaps} href={urlGoogleMaps} rel="noreferrer" target="_blank">
                      {t('tiendas.locator', 'get.diretions', 'Get directions')}
                      <img src="/assets-new/img/shops/arrow.png" alt="chevron-right" />
                    </a>
                    <br></br>
                  </>
                )}
                {bookingAvailability && (
                  <button className={styles.bookAppointmentBtn} onClick={onClickAppointmentBtn}>
                    {t('tienda', 'book.your.appointment', 'Book your appointment')}
                    <img src="/assets-new/img/shops/arrow.png" alt="chevron-right" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {imagesStories.length > 0 && isMobile && (
        <>
          <div
            className={`${styles.storyBlock} ${styles.storyBlockText}`}
            dangerouslySetInnerHTML={{ __html: t('tienda', `historia.descripcion.${id}`) }}
          ></div>
          <div className={styles.storyBlock}>
            {urlGoogleMaps && (
              <>
                <a className={styles.urlGoogleMaps} href={urlGoogleMaps} rel="noreferrer" target="_blank">
                  {t('tiendas.locator', 'get.diretions', 'Get directions')}
                  <img src="/assets-new/img/shops/arrow.png" alt="chevron-right" />
                </a>
                <br></br>
              </>
            )}
            {bookingAvailability && (
              <button className={styles.bookAppointmentBtn} onClick={onClickAppointmentBtn}>
                {t('tienda', 'book.your.appointment', 'Book your appointment')}
                <img src="/assets-new/img/shops/arrow.png" alt="chevron-right" />
              </button>
            )}
          </div>
        </>
      )}
      {shoppingLinks?.length > 0 && (
        <div className={styles.keepShoppingDiv}>
          <h2>{t('tiendas.locator', 'keep.shopping', 'Keep shopping online?')}</h2>
          <div className={styles.linksDiv}>
            {shoppingLinks?.map((menu) => (
              <a
                key={menu?.title}
                href={menu?.slug || `${baseUrl}/${locale}`}
                data-tracking={menu?.tracking?.replace('main-menu', 'shop-locator')}
                data-trackingga4={menu?.trackingGA4?.replace('main-menu', 'shop-locator')}
              >
                {menu?.title?.toLowerCase() !== 'CAMPERLAB' ? menu?.title : 'CAMPERLAB'}
              </a>
            ))}
          </div>
        </div>
      )}
      {allStoresByCountry && allStoresByCountry.length > 0 && (
        <div className={styles.keepShoppingDiv}>
          <h2>{t('tiendas.locator', 'stores.by.country', 'Camper stores by country')}</h2>
          <div className={`${styles.linksDivCountries} ${styles.storesByCountry}`}>
            {allStoresByCountry
              .sort((a, b) => a.country.localeCompare(b.country))
              .map((camperStore) => (
                <a key={camperStore.country} href={normalizeUrl(`/${camperStore.url}`)}>
                  {camperStore?.country}
                </a>
              ))}
          </div>
        </div>
      )}
      {false && relatedStores.length > 0 && (
        <div className={styles.otherStores}>
          <div className={styles.otherStoresHeader}>
            <p className={styles.otherStoresTitle}>
              {t('tienda', 'other.stores', 'Other stores in')} <b>{cityName}</b>
            </p>
            <a className={styles.storesLink}></a>
          </div>
          <StoresGrid stores={relatedStores} withMap={false} handleViewInMap={false} />
        </div>
      )}
      <div className={styles.breadcrumbs}>
        <Breadcrumb
          locale={locale}
          shallow={true}
          useNextLink={false}
          changeLast={() => name}
          onClick={() => true}
          fragments={breadcrumbFragments}
          withLdJson={true}
        />
      </div>
      <AppointmentModal store={store} visible={appointmentVisible} onClose={handleBookAppointment} />
    </Layout>
  );
}

StorePage.propTypes = {
  footerProps: any,
  tnmProps: any,
  store: object,
  isMobile: bool,
  allStoresByCountry: array,
  shoppingLinks: array,
};

StorePage.i18nPage = 'ShoplocatorStore';

export async function getServerSideProps(context) {
  const locale = ['default', 'int'].includes(context.locale) ? 'es_ES' : context.locale;

  if (
    getDomainValue({
      camper: false,
      nnormal: true,
      camperlab: true,
    })
  ) {
    return {
      redirect: {
        destination: `/${locale}/shops`,
        permanent: false,
      },
    };
  }
  const { id } = context.params;
  const { defaultProps } = await initCMS({ context, locale });
  delete defaultProps.ribbon;
  const { length } = id.split('-');
  const realID = id.split('-')[length - 1];

  const isMobile = getIsMobileSSR(context);

  const menuTypes = ['W', 'M', 'K', 'C'];
  const shoppingLinks = defaultProps?.tnmProps?.menus?.filter((menu) => menuTypes.includes(menu.menuType));
  const womenLink = shoppingLinks?.filter((menu) => menu.menuType === 'W').shift();
  if (womenLink) {
    const accessoriesLink = womenLink.groups?.filter((menu) => menu.tracking.includes('accessories'))?.shift();
    const apparelLink = womenLink.groups?.filter((menu) => menu.tracking.includes('apparel'))?.shift();
    let camperlabLink = null;
    if (shoppingLinks[shoppingLinks.length - 1].menuType === 'C') {
      camperlabLink = shoppingLinks.pop();
    }
    if (accessoriesLink) {
      shoppingLinks.push(accessoriesLink);
    }
    if (apparelLink) {
      shoppingLinks.push(apparelLink);
    }
    if (camperlabLink) {
      shoppingLinks.push(camperlabLink);
    }
  }

  let allStoresByCountry = [];
  try {
    allStoresByCountry = await getAllStoresByCountry(locale);
  } catch (rsError) {
    console.error(rsError);
  }

  try {
    const store = await getStore({
      context,
      profile: locale,
      id: realID,
    });

    const lang = getLang(context?.locale);
    const market = store?.countryCode?.toUpperCase();
    const profile = `${lang}_${market}`;
    const storeProfileData = await checkProfile({ profile });

    store.currency = storeProfileData?.currencySap;

    return {
      props: {
        defaultProps,
        store,
        isMobile,
        allStoresByCountry,
        shoppingLinks: shoppingLinks || [],
      },
    };
  } catch (e) {
    return {
      notFound: true,
    };
  }
}
